import { useEffect, useState } from 'react';
import { Download, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import type { AccountReport } from '../../types';
import { formatDate, formatFileSize } from '../../lib/format';
import { Spinner, EmptyState, ConfirmDialog } from '../../components/ui';

export default function AccountReports() {
  const [reports, setReports] = useState<AccountReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get<AccountReport[]>('/account-reports')
      .then(setReports)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/account-reports/${deleteId}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">Account Reports</h1>
      <p className="text-sm text-gray-500 mb-6">Reports uploaded by the account team.</p>

      {loading ? (
        <Spinner label="Loading reports..." />
      ) : reports.length === 0 ? (
        <EmptyState message="No account reports uploaded yet." />
      ) : (
        <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">File</th>
                <th className="px-4 py-3">Uploaded By</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{r.title}</td>
                  <td className="px-4 py-3 text-gray-500">{r.description || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{r.filename}</td>
                  <td className="px-4 py-3">{r.user_name}</td>
                  <td className="px-4 py-3 text-gray-500">{formatFileSize(r.size)}</td>
                  <td className="px-4 py-3 text-gray-500">{formatDate(r.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <a
                        href={`/api/account-reports/${r.id}/download`}
                        download
                        className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-300 text-xs hover:bg-gray-50"
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                      </a>
                      <button onClick={() => setDeleteId(r.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Report"
        message="Are you sure you want to delete this account report?"
      />
    </div>
  );
}
